import { Shift, UserRole } from '@nextforce/api'
import { Divider, Text, makeStyles } from '@rneui/themed'
import { onlineManager } from '@tanstack/react-query'
import { t } from 'app/i18l'
import React from 'react'
import { View } from 'react-native'
import { CommandButtons } from 'shared/components/command_buttons'
import { BUTTON_HEIGHT, RoundedButton } from 'shared/components/rounded-button'
import { SHIFT_NOTE_HEIGHT, ShiftNoteView } from 'shared/components/shift/note'
import { PastShiftRating } from 'shared/components/shift/past_shift_rating'
import {
  PAST_SHIFT_STATUS_HEIGHT,
  PastShiftStatusView,
} from 'shared/components/shift/past_shift_status'
import { ShiftPayView } from 'shared/components/shift/pay'
import { ShiftPositionView } from 'shared/components/shift/position'
import { ShiftStoreView } from 'shared/components/shift/store'
import { ShiftTimeRangeDurationView } from 'shared/components/shift/time_range_duration'
import { ShiftTotalHoursView } from 'shared/components/shift/total_hours'
import {
  CurrentShiftViewProps,
  PastShiftViewProps,
  ShiftViewProps,
} from '../types'

const HELPER_PROXY_ASSIGNMENT_ROW_HEIGHT = 15

export function HelperScheduleShift({
  shift,
  onShiftDetails,
  onCommandButtonPress,
}: ShiftViewProps) {
  return shift.isPastShift ? (
    <PastShiftView shift={shift} onCommandButtonPress={onCommandButtonPress} />
  ) : (
    <CurrentShiftView shift={shift} onShiftDetails={onShiftDetails} />
  )
}

function CurrentShiftView({ shift, onShiftDetails }: CurrentShiftViewProps) {
  const styles = useStyles()
  return (
    <View style={styles.container}>
      <ShiftPositionView position={shift.position} />
      <ShiftPayView
        amount={shift?.shiftAmount}
        bonus={shift.bonus?.amount}
        shiftAmountExplanationMessage={shift.shiftAmountExplanationMessage}
      />
      <ShiftStoreView shift={shift} showApprover={false} />
      {shift.helperProxyAssignmentMessage ? (
        <>
          <Divider style={styles.divider} />
          <Text style={styles.proxyText}>{t('crossStoreClockInOut')}</Text>
        </>
      ) : null}
      <Divider style={styles.divider} />
      <ShiftTimeRangeDurationView shift={shift} />
      <Divider style={styles.divider} />
      <ShiftTotalHoursView hours={shift.user?.totalScheduledHours} />
      <ShiftNoteView note={shift.note} />
      {shift.route && (
        <RoundedButton
          disabled={!onlineManager.isOnline()}
          title={t('makeChanges')}
          emphasized
          onPress={() => {
            onShiftDetails(shift)
          }}
        />
      )}
    </View>
  )
}

function PastShiftView({ shift, onCommandButtonPress }: PastShiftViewProps) {
  const styles = useStyles()
  const isWorkedShift = !!shift.actualHours
  return (
    <View style={styles.container}>
      <ShiftPositionView position={shift.position} />
      <ShiftPayView
        amount={isWorkedShift ? shift.shiftAmount : 0}
        bonus={shift.bonus?.amount}
      />
      <ShiftStoreView shift={shift} showApprover={false} />
      <Divider style={styles.divider} />
      <ShiftTimeRangeDurationView shift={shift} />
      <Divider style={styles.divider} />
      <ShiftTotalHoursView hours={shift.weeklyHours} />
      <ShiftNoteView note={shift.note} />
      <PastShiftStatusView isWorkedShift={isWorkedShift} />
      <CommandButtons
        disabled={!onlineManager.isOnline()}
        commands={shift.commands}
        onButtonPress={onCommandButtonPress}
      />
      <PastShiftRating rating={shift?.rating} />
    </View>
  )
}

const HEIGHT = 225

export function getHelperScheduleShiftHeight(role: UserRole, shift: Shift) {
  return (
    HEIGHT +
    (shift.route ? BUTTON_HEIGHT : PAST_SHIFT_STATUS_HEIGHT) +
    (shift.note ? SHIFT_NOTE_HEIGHT : 0) +
    (shift.helperProxyAssignmentMessage
      ? HELPER_PROXY_ASSIGNMENT_ROW_HEIGHT
      : 0)
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    justifyContent: 'space-between',
    padding: 5,
  },
  divider: {
    backgroundColor: theme.colors?.grey4,
  },
  proxyText: {
    color: theme.colors.grey1,
    height: HELPER_PROXY_ASSIGNMENT_ROW_HEIGHT,
    textAlignVertical: 'center',
  },
}))
